import { closeSnackbar, enqueueSnackbar } from "notistack";
import querystring from "querystring";
import React, { createContext, ReactNode, useContext } from "react";
import { AuthContextProps } from "react-oidc-context";

import { ApiDirectory, ApiDirectoryEntryName } from "../api/ApiDirectory";
import APIconButton from "../components/buttons/APIconButton";
import { downloadFile } from "../helpers/download";
import { ErrorCode } from "../types/Error";

const REQUEST_DETAILS_HEADER = "Request-Details";

export type ErrorCause = {
  errorCode: ErrorCode;
};

export async function handleErrorResponse(response: Response) {
  const requestDetails = response.headers.get(REQUEST_DETAILS_HEADER);
  let error;
  let errorCause: ErrorCause | undefined = undefined;

  try {
    error = await response.text();

    const parsedError = JSON.parse(error);
    errorCause = { errorCode: parsedError.errorCode };
  } catch (err) {
    error = err;
  }

  throw new Error(
    `${JSON.stringify(error)}; requestDetails: ${requestDetails}`,
    { cause: errorCause }
  );
}

export type FetchOptions = {
  requestOptions?: RequestInit & { excludeContentTypeHeader?: boolean };
  queryParams?: Record<string, string | undefined>;
  responseOptions?: {
    responseType?: "json" | "blob" | "fileUrl";
  };
};

export type ClientIPData = {
  ip: string;
};

// Define the context with a fetch function
const ApiContext = createContext<{
  fetchWithAuth: <T>(
    directoryEntry: ApiDirectoryEntryName,
    entityId?: string,
    options?: FetchOptions
  ) => Promise<T>;
  fetchFile: (
    apiEntry: ApiDirectoryEntryName,
    entityId: string,
    filename: string,
    options?: FetchOptions
  ) => Promise<void>;
}>({
  fetchWithAuth: () => Promise.reject("API client not configured"),
  fetchFile: () => Promise.reject("API client not configured"),
});

// Custom hook to use the API context
export const useApi = () => {
  return useContext(ApiContext);
};

function attachQueryString(path: string, queryString: string | null) {
  return queryString ? `${path}?${queryString}` : path;
}

// API Provider component
export const ApiProvider: React.FC<{
  auth: AuthContextProps;
  baseUrl: string;
  apiDirectory: ApiDirectory;
  children: ReactNode;
}> = ({ auth, baseUrl, apiDirectory, children }) => {
  const useAccessToken = () => {
    const getValidAccessToken = async () => {
      if (!auth.user) return null;

      const now = Math.floor(Date.now() / 1000);

      // small buffer (e.g. 60 seconds)
      const isExpired =
        auth.user.expires_at && auth.user.expires_at <= now + 60;

      if (isExpired) {
        try {
          const refreshedUser = await auth.signinSilent();
          return refreshedUser?.access_token;
        } catch (e) {
          console.error("Silent renew failed", e);
          await auth.signinRedirect(); // fallback
          return null;
        }
      }

      return auth.user.access_token;
    };

    return { getValidAccessToken };
  };

  const fetchWithAuth = async (
    directoryEntry: ApiDirectoryEntryName,
    entityId?: string,
    options: FetchOptions = {}
  ) => {
    const apiEntry = apiDirectory[directoryEntry];
    const { getValidAccessToken } = useAccessToken();

    if (apiEntry) {
      let path = apiEntry(entityId);

      if (options.queryParams) {
        const filteredParams = Object.fromEntries(
          Object.entries(options.queryParams).filter(
            ([, value]) => value !== undefined
          )
        );
        const queryString = querystring.stringify(filteredParams);

        path = attachQueryString(path, queryString);
      }

      const accessToken = await getValidAccessToken();

      if (!accessToken) {
        await auth.signinRedirect();
      }
      const defaultOptions = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ...(!options.requestOptions?.excludeContentTypeHeader && {
            ["Content-Type"]: "application/json",
          }),
        },
        ...options.requestOptions,
      };
      const response = await fetch(`${baseUrl}${path}`, defaultOptions);

      if (!response.ok) {
        await handleErrorResponse(response);
      }

      const responseType = options.responseOptions?.responseType || "json";

      switch (responseType) {
        case "json":
          return response.json();
        case "blob":
          return response.blob();
        case "fileUrl": {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);

          return imageUrl;
        }
      }
    } else {
      throw Error(
        `No configured endpoint, entry: ${directoryEntry}, entityId: ${entityId}`
      );
    }
  };

  const fetchFile = async (
    apiEntry: ApiDirectoryEntryName,
    entityId: string,
    filename: string,
    options?: FetchOptions
  ) => {
    try {
      const file: Blob = await fetchWithAuth(apiEntry, entityId, {
        responseOptions: { responseType: "blob" },
        ...options,
      });

      downloadFile(file, filename);
    } catch (e) {
      enqueueSnackbar(`There was an issue downloading the file.`, {
        variant: "error",
        action: (key) => (
          <APIconButton
            iconName="close"
            onClick={() => closeSnackbar(key)}
            color="inherit"
          />
        ),
      });
    }
  };

  return (
    <ApiContext.Provider value={{ fetchWithAuth, fetchFile }}>
      {children}
    </ApiContext.Provider>
  );
};
